<template>
  <SoButton
    title="Eligible for Exchange Upload"
    data-testid="show-eligible-for-exchange-upload"
    prepend-icon="upload"
    large
    @click="showModal = true"
  />
  <SoDialog
    v-model="showModal"
    card-title="Bulk upload smart meters eligible for exchange"
    data-testid="eligible-for-exchange-upload-modal"
  >
    <div>
      <p class="mb-4">
        Upload a CSV file to add or update meters in the eligible-for-exchange
        list. Download the template below to see the expected format.
      </p>
    </div>
    <SoFileInputField
      v-model="file"
      class="mb-4"
      file-types="csv"
      label="Upload CSV"
      placeholder="Select a file"
    />
    <div v-if="!isLoading">
      <SoAlert
        v-if="alertState"
        :text="alertState.text"
        :type="alertState.type"
        data-testid="eligible-for-exchange-alert"
      />
      <div class="d-flex justify-end my-3">
        <SoExportCsvButton
          v-if="rowErrors.length > 0"
          :data="errorDownloadConfig.data"
          :labels="errorDownloadConfig.headers"
          :file-name="errorDownloadConfig.fileName"
          button-text="Download Errors"
          prepend-icon="download"
          data-testid="eligible-for-exchange-error-download"
          @export="prepareErrorDownload"
        />
      </div>
    </div>
    <div class="d-flex justify-space-between mt-4">
      <SoExportCsvButton
        :data="templateDownloadConfig.data"
        :labels="templateDownloadConfig.headers"
        :file-name="templateDownloadConfig.fileName"
        button-text="Download Template"
        prepend-icon="download"
        data-testid="eligible-for-exchange-template-download"
        @export="triggerTemplateDownload"
      />
      <div class="d-flex gap-2">
        <SoButton title="Cancel" large @click="showModal = false" />
        <SoButton
          title="Upload"
          type="primary"
          large
          :disabled="file.length === 0"
          :loading="isLoading"
          data-testid="eligible-for-exchange-submit"
          @click="uploadFile"
        />
      </div>
    </div>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoFileInputField from "@/components/formControls/SoFileInputField.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoAlert, { AlertTypes } from "@/components/SoAlert.vue"
  import SoExportCsvButton from "@/components/dataTable/SoExportCsvButton.vue"
  import { BulkSmartMetersEligibleForExchangeUploadMutator } from "@/mutators/smartMeter/BulkSmartMetersEligibleForExchangeUploadMutator"
  import { SmartMeterEligibleForExchangeErrorExporter } from "@/models/meterpoint/smartMetersEligibleForExchangeErrorExporter"
  import type {
    SmartMeterEligibleForExchangeRow,
    SmartMeterEligibleForExchangeRowError,
    SmartMetersEligibleForExchangeUploadResponse,
  } from "@/api/backofficeRest/generated/model"
  import { errorReporter } from "@/services/ErrorService"
  import { formatError, parseFile, UploadResult } from "./fileUploaderHelper"
  import { SoDataTableHeader } from "@/types/components/SoDataTable"

  export const smartMeterEligibleForExchangeHeaders: SoDataTableHeader<
    keyof SmartMeterEligibleForExchangeRow
  >[] = [
    { title: "mpxn", key: "mpxn" },
    { title: "meter_serial_number", key: "meterSerialNumber" },
    { title: "fuel", key: "fuel" },
    { title: "meter_type", key: "meterType" },
    {
      title: "exchange_eligibility_reason",
      key: "exchangeEligibilityReason",
    },
    { title: "confirmed_etc_waived", key: "confirmedEtcWaived" },
  ]

  export default defineComponent({
    components: {
      SoFileInputField,
      SoDialog,
      SoButton,
      SoAlert,
      SoExportCsvButton,
    },
    data() {
      return {
        showModal: false,
        file: [] as File[],
        isLoading: false,
        uploadResult:
          null as UploadResult<SmartMeterEligibleForExchangeRowError> | null,
        hasNetworkError: false,
        errorExportData: null as SmartMeterEligibleForExchangeRowError[] | null,
        uploadedFileName: "",
        templateData: {
          isLoading: false,
          items: [] as Record<string, string>[],
        },
        uploadMutator: BulkSmartMetersEligibleForExchangeUploadMutator.upload(),
      }
    },
    computed: {
      rowErrors(): SmartMeterEligibleForExchangeRowError[] {
        return this.uploadResult?.errors ?? []
      },
      alertState(): { text: string; type: AlertTypes } | null {
        return formatError<SmartMeterEligibleForExchangeRowError>(
          this.hasNetworkError,
          this.uploadResult,
        )
      },
      errorDownloadConfig() {
        const exporter = new SmartMeterEligibleForExchangeErrorExporter(
          this.errorExportData,
          this.uploadedFileName,
        )
        return exporter.getConfig()
      },
      templateDownloadConfig() {
        return {
          data: this.templateData,
          headers: [
            { title: "mpxn", key: "mpxn" },
            { title: "meter_serial_number", key: "meter_serial_number" },
            { title: "fuel", key: "fuel" },
            { title: "meter_type", key: "meter_type" },
            {
              title: "exchange_eligibility_reason",
              key: "exchange_eligibility_reason",
            },
            { title: "confirmed_etc_waived", key: "confirmed_etc_waived" },
          ],
          fileName: "smart-meters-eligible-for-exchange-template.csv",
        }
      },
    },
    watch: {
      showModal(val: boolean) {
        if (!val) {
          this.file = []
          this.uploadResult = null
          this.hasNetworkError = false
          this.errorExportData = null
          this.uploadedFileName = ""
        }
      },
    },
    methods: {
      triggerTemplateDownload() {
        this.templateData = {
          isLoading: false,
          items: [
            {
              mpxn: "1234567890123",
              meter_serial_number: "A1B2C3D4",
              fuel: "ELECTRICITY",
              meter_type: "SMETS1",
              exchange_eligibility_reason: "DCC Exclusion List",
              confirmed_etc_waived: "TRUE",
            },
            {
              mpxn: "123456789",
              meter_serial_number: "A1B2C3D4E5F6G7H8",
              fuel: "GAS",
              meter_type: "SMETS1",
              exchange_eligibility_reason: "DCC Exclusion List",
              confirmed_etc_waived: "TRUE",
            },
            {
              mpxn: "1234567890124",
              meter_serial_number: "A1B2C3D4",
              fuel: "ELECTRICITY",
              meter_type: "SMETS2",
              exchange_eligibility_reason: "Smart Metering Investigation",
              confirmed_etc_waived: "TRUE",
            },
          ],
        }
        this.$nextTick(() => {
          this.templateData = { isLoading: false, items: [] }
        })
      },
      prepareErrorDownload() {
        this.errorExportData = this.rowErrors
      },
      async uploadFile() {
        if (this.file.length === 0) return

        this.isLoading = true
        this.uploadResult = null
        this.hasNetworkError = false
        this.errorExportData = null

        try {
          const csvFile = this.file[0]
          this.uploadedFileName = csvFile.name
          const text = await csvFile.text()
          const parsed = parseFile<SmartMeterEligibleForExchangeRow>(
            text,
            smartMeterEligibleForExchangeHeaders,
          )

          if (parsed.error !== null) {
            this.uploadResult = {
              totalCount: 0,
              addedCount: 0,
              updatedCount: 0,
              errorCount: 0,
              errors: [],
              fileError: parsed.error,
            }
            return
          }

          const result = (await this.uploadMutator.mutateAsync({
            rows: parsed.rows,
            fileName: csvFile.name,
          })) as SmartMetersEligibleForExchangeUploadResponse

          this.uploadResult = {
            totalCount: result.totalCount ?? 0,
            addedCount: result.addedCount ?? 0,
            updatedCount: result.updatedCount ?? 0,
            errorCount: result.errorCount ?? 0,
            errors: result.errors ?? [],
            fileError: result.fileError,
          }
        } catch (error) {
          this.hasNetworkError = true
          errorReporter.report(error)
        } finally {
          this.isLoading = false
        }
      },
    },
  })
</script>
